<template>
  <v-container fluid>
    <v-card dark outlined>
      <v-card-title class="secondary py-1 text-subtitle-1">
        <v-icon left dense>mdi-account-group</v-icon>
        Dashboard Groups
        <v-spacer />
        <v-btn x-small dark class="mr-2" @click="openCreate">
          <v-icon left x-small>mdi-plus</v-icon>
          Create Group
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="search"
          label="Search group name"
          dense
          outlined
          clearable
          hide-details
          class="mb-3"
          style="max-width: 320px"
        />
        <v-data-table
          :headers="headers"
          :items="filtered"
          :loading="loading"
          class="elevation-5"
        >
          <template v-slot:item.MemberCount="{ item }">
            {{ (item.Members || []).length }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon small class="mr-2" @click="openEdit(item)">mdi-pencil</v-icon>
            <v-icon small @click="confirmDelete(item)">mdi-delete</v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="createDialog" max-width="480" persistent>
      <v-card dark>
        <v-card-title>Create Group</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="createName"
            label="Group name"
            dense
            outlined
            class="mt-3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="createDialog = false">Cancel</v-btn>
          <v-btn color="secondary" :loading="saving" @click="createGroup">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog" max-width="900" persistent scrollable>
      <v-card dark>
        <v-card-title>Edit Group — {{ editGroup.UserGroupName }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editGroup.UserGroupName"
            label="Group name"
            dense
            outlined
            class="mt-3 mb-2"
          />
          <v-tabs v-model="editTab" dark>
            <v-tab>Manage Members</v-tab>
            <v-tab>Manage Permissions</v-tab>
          </v-tabs>
          <v-tabs-items v-model="editTab" dark>
            <v-tab-item>
              <v-autocomplete
                v-model="addMemberId"
                :items="userOptions"
                item-text="UserId"
                item-value="UserId"
                label="Add existing user"
                dense
                outlined
                class="mt-3"
              />
              <v-btn small color="secondary" class="mb-3" @click="addMember">
                Add Member
              </v-btn>
              <v-list dense dark>
                <v-list-item v-for="m in editGroup.Members || []" :key="m">
                  <v-list-item-content>{{ m }}</v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon @click="removeMember(m)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-tab-item>
            <v-tab-item>
              <div class="mt-3">
                <v-select
                  v-model="permissionsYear"
                  :items="yearOptions"
                  label="Filter by year"
                  dense
                  outlined
                  style="max-width: 200px"
                  class="mb-3"
                />
                <v-btn small color="secondary" class="mr-2" @click="addPermDialog = true">
                  <v-icon left small>mdi-plus</v-icon>
                  Add Permissions
                </v-btn>
                <v-btn small outlined @click="managePermDialog = true">
                  <v-icon left small>mdi-eye</v-icon>
                  View / Manage Permissions
                </v-btn>
                <span class="caption grey--text ml-3">
                  {{ (editGroup.Events || []).length }} event permission(s)
                </span>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editDialog = false">Cancel</v-btn>
          <v-btn color="secondary" :loading="saving" @click="saveGroup">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <add-permissions-dialog
      v-model="addPermDialog"
      :events="editGroup.Events"
      :default-year="permissionsYear"
      @update:events="onEventsUpdated"
    />

    <manage-permissions-dialog
      v-model="managePermDialog"
      :events="editGroup.Events"
      :can-edit="true"
      :year-filter="permissionsYear"
      @update:events="onEventsUpdated"
    />

    <delete-confirmation-dialog
      v-model="deleteDialog"
      title="Delete this group?"
      @confirm="deleteGroup"
      @cancel="deleteDialog = false"
    />
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import { dashboardAPI } from "@/services";
import AddPermissionsDialog from "@/components/admin/AddPermissionsDialog.vue";
import ManagePermissionsDialog from "@/components/admin/ManagePermissionsDialog.vue";
import DeleteConfirmationDialog from "@/components/dialogs/DeleteConfirmationDialog.vue";
import iziToast from "izitoast/dist/js/iziToast.min.js";
import "izitoast/dist/css/iziToast.min.css";

export default {
  name: "DashboardGroups",
  components: {
    AddPermissionsDialog,
    ManagePermissionsDialog,
    DeleteConfirmationDialog,
  },
  data() {
    const y = new Date().getFullYear();
    return {
      loading: false,
      saving: false,
      groups: [],
      userOptions: [],
      search: "",
      headers: [
        { text: "Group Name", value: "UserGroupName" },
        { text: "Members", value: "MemberCount" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      createDialog: false,
      createName: "",
      editDialog: false,
      editTab: 0,
      editGroup: { UserGroupId: "", UserGroupName: "", Members: [], Events: [] },
      addMemberId: null,
      deleteDialog: false,
      deleteTarget: null,
      yearOptions: [y + 1, y, y - 1, y - 2, y - 3, y - 4, y - 5],
      permissionsYear: y,
      addPermDialog: false,
      managePermDialog: false,
    };
  },
  computed: {
    ...mapState("filter", ["IsAdmin"]),
    filtered() {
      const q = (this.search || "").toLowerCase();
      if (!q) return this.groups;
      return this.groups.filter((g) =>
        (g.UserGroupName || "").toLowerCase().includes(q)
      );
    },
  },
  created() {
    if (!this.IsAdmin) {
      this.$router.replace("/");
      return;
    }
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const api = dashboardAPI.getDashboardAccessAPI();
        const [groups, users] = await Promise.all([
          api.getDashboardGroups(),
          api.getDashboardUsers(),
        ]);
        this.groups = groups.Groups || [];
        this.userOptions = users.Users || [];
      } catch (e) {
        iziToast.error({ title: "Groups", message: "Failed to load", position: "topRight" });
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.createName = "";
      this.createDialog = true;
    },
    async createGroup() {
      this.saving = true;
      try {
        const data = await dashboardAPI.getDashboardAccessAPI().postDashboardGroups({
          Action: "Create",
          UserGroupName: this.createName,
        });
        this.createDialog = false;
        iziToast.success({ title: "Groups", message: "Created", position: "topRight" });
        await this.load();
        if (data.Group) this.openEdit(data.Group);
      } catch (e) {
        iziToast.error({
          title: "Groups",
          message: e?.response?.data?.Error || "Create failed",
          position: "topRight",
        });
      } finally {
        this.saving = false;
      }
    },
    openEdit(item) {
      this.editGroup = JSON.parse(JSON.stringify(item));
      if (!this.editGroup.Members) this.editGroup.Members = [];
      if (!this.editGroup.Events) this.editGroup.Events = [];
      this.editTab = 0;
      this.addMemberId = null;
      this.permissionsYear = new Date().getFullYear();
      this.addPermDialog = false;
      this.managePermDialog = false;
      this.editDialog = true;
    },
    onEventsUpdated(next) {
      this.editGroup.Events = next;
    },
    addMember() {
      if (!this.addMemberId) return;
      if (!this.editGroup.Members.includes(this.addMemberId)) {
        this.editGroup.Members.push(this.addMemberId);
      }
      this.addMemberId = null;
    },
    removeMember(uid) {
      this.editGroup.Members = this.editGroup.Members.filter((m) => m !== uid);
    },
    async saveGroup() {
      this.saving = true;
      try {
        await dashboardAPI.getDashboardAccessAPI().postDashboardGroups({
          Action: "Update",
          UserGroupId: this.editGroup.UserGroupId,
          UserGroupName: this.editGroup.UserGroupName,
          Members: this.editGroup.Members,
          Events: this.editGroup.Events,
        });
        this.editDialog = false;
        iziToast.success({ title: "Groups", message: "Saved", position: "topRight" });
        this.load();
      } catch (e) {
        iziToast.error({
          title: "Groups",
          message: e?.response?.data?.Error || "Save failed",
          position: "topRight",
        });
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async deleteGroup() {
      try {
        await dashboardAPI.getDashboardAccessAPI().postDashboardGroups({
          Action: "Delete",
          UserGroupId: this.deleteTarget.UserGroupId,
        });
        this.deleteDialog = false;
        iziToast.success({ title: "Groups", message: "Deleted", position: "topRight" });
        this.load();
      } catch (e) {
        iziToast.error({
          title: "Groups",
          message: e?.response?.data?.Error || "Delete failed",
          position: "topRight",
        });
      }
    },
  },
};
</script>
